/**
 * Timezone utility functions for converting UTC timestamps to Swiss time
 */

/**
 * Converts a UTC timestamp string to Swiss time (UTC+1 in winter, UTC+2 in summer)
 * @param utcTimestamp - UTC timestamp string (e.g., "2025-08-29 05:46:36")
 * @returns Swiss time Date object
 */
export function convertUTCToSwissTime(utcTimestamp: string): Date {
  // Parse the UTC timestamp correctly by replacing space with 'T' and adding 'Z'
  const utcDate = new Date(utcTimestamp.replace(' ', 'T') + 'Z');
  
  // Check if the date is valid
  if (isNaN(utcDate.getTime())) {
    console.warn('Invalid UTC timestamp:', utcTimestamp);
    return new Date(); // Return current date as fallback
  }
  
  // JavaScript automatically handles timezone conversion when we create a Date from UTC
  // and then call methods like getHours(), getMinutes() - they return local time
  // No need to manually add timezone offset - JavaScript does this automatically
  return utcDate;
}

// Note: Manual timezone offset functions removed since JavaScript handles timezone conversion automatically

/**
 * Formats a Swiss time Date object to a readable string
 * @param swissDate - Swiss time Date object
 * @returns Formatted string (e.g., "29.08.2025 07:46")
 */
export function formatSwissTime(swissDate: Date): string {
  const day = swissDate.getDate().toString().padStart(2, '0');
  const month = (swissDate.getMonth() + 1).toString().padStart(2, '0');
  const year = swissDate.getFullYear();
  const hours = swissDate.getHours().toString().padStart(2, '0');
  const minutes = swissDate.getMinutes().toString().padStart(2, '0');
  
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Gets the Swiss time components (hours, minutes) from a UTC timestamp
 * @param utcTimestamp - UTC timestamp string
 * @returns Object with hours and minutes in Swiss time
 */
export function getSwissTimeComponents(utcTimestamp: string): { hours: number; minutes: number } {
  // Parse the UTC timestamp correctly by replacing space with 'T' and adding 'Z'
  const utcDate = new Date(utcTimestamp.replace(' ', 'T') + 'Z');
  
  // Check if the date is valid
  if (isNaN(utcDate.getTime())) {
    console.warn('Invalid UTC timestamp:', utcTimestamp);
    return { hours: 0, minutes: 0 };
  }
  
  // JavaScript automatically handles timezone conversion - getHours() and getMinutes()
  // return the local time (Swiss time in this case)
  return {
    hours: utcDate.getHours(),
    minutes: utcDate.getMinutes()
  };
}
